import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { formatChinaDate } from '../utils/date';
import PageHero from '../components/layout/PageHero';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';

type Position = {
  _id: string;
  title: string;
  level: 'Master' | 'PhD' | 'Postdoc' | 'Research Assistant' | 'Other';
  direction?: string;
  status: 'open' | 'closed';
  description?: string;
  deadline?: string; // ISO date from API
};

// Detect whether description contains HTML tags
const isHtml = (s: string) => /<[a-z][\s\S]*>/i.test(s);

const DEFAULT_INTRO =
  'We welcome talented candidates in materials, applied physics, and electrochemical energy research. Opportunities are available for postgraduate students and postdoctoral researchers.';

const Recruitment: React.FC = () => {
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [intro, setIntro] = useState(DEFAULT_INTRO);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);

  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const res = await api.get<Position[]>('/recruitment-positions');
        setPositions(res.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchPositions();
  }, []);

  useEffect(() => {
    api.get<{ recruitment: string }>('/page-intros').then((res) => {
      const v = res.data?.recruitment?.trim();
      if (v) setIntro(v);
    }).catch(() => {});
  }, []);

  const levelOptions: Array<{ id: string; label: string; value: Position['level'] }> = [
    { id: 'postdoc', label: 'Postdoc', value: 'Postdoc' },
    { id: 'phd', label: 'PhD', value: 'PhD' },
    { id: 'master', label: 'Master', value: 'Master' },
    { id: 'ra', label: 'Research Assistant', value: 'Research Assistant' },
  ];

  const toggleLevel = (value: Position['level']) => {
    setSelectedLevels((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const visiblePositions = positions
    .filter((p) => p.status !== 'closed')
    .filter((p) => selectedLevels.length === 0 || selectedLevels.includes(p.level));

  return (
    <section className="bg-surface text-on-surface min-h-full">
      <PageHero title="Recruitment" intro={intro} sectionLabel="Open Positions" />
      <main className="pb-20 pt-12">

        <section className="mx-auto max-w-screen-2xl px-8">
          <div className="flex flex-col gap-12 md:flex-row">
            <aside className="w-full md:w-64 shrink-0">
              <div className="um-card-soft sticky top-32 bg-surface-container-highest/30 p-6">
                <h3 className="mb-4 border-b border-outline-variant/30 pb-2 text-2xl font-semibold leading-tight text-primary">
                  Filters
                </h3>
                <div className="space-y-3">
                  <h4 className="text-xs uppercase tracking-wider text-on-surface-variant font-semibold">
                    Position Level
                  </h4>
                  {levelOptions.map((opt) => (
                    <label key={opt.id} className="flex cursor-pointer items-center gap-2 text-sm text-on-surface">
                      <input
                        type="checkbox"
                        checked={selectedLevels.includes(opt.value)}
                        onChange={() => toggleLevel(opt.value)}
                        className="um-check"
                      />
                      <span className="hover:text-primary transition-colors">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </aside>

            <div className="flex-grow space-y-6">
              <div className="mb-4 flex items-end justify-between">
                <h2 className="text-3xl font-semibold leading-tight text-on-background md:text-4xl">
                  Open Positions <span className="ml-2 text-2xl font-normal text-on-surface-variant">({visiblePositions.length})</span>
                </h2>
              </div>

              {loading ? (
                <p className="text-on-surface-variant">Loading...</p>
              ) : visiblePositions.length === 0 ? (
                <div className="um-card bg-surface-container-low p-12 text-center">
                  <h3 className="text-3xl font-semibold leading-tight text-on-surface mb-2">No open positions right now</h3>
                  <p className="text-on-surface-variant mb-6">
                    No positions match the current filters. Contact us for upcoming opportunities.
                  </p>
                  <Link to="/contact" className="um-btn-link">
                    Contact us for updates
                  </Link>
                </div>
              ) : (
                visiblePositions.map((pos) => (
                  <article
                    key={pos._id}
                    className="um-card group bg-surface-container-lowest p-8 transition-colors hover:border-outline-variant/40"
                  >
                    <div className="flex flex-col justify-between gap-6 lg:flex-row">
                      <div className="flex-grow">
                        <div className="mb-3 flex flex-wrap items-center gap-3">
                          <span className="inline-block rounded-full bg-secondary-container px-3 py-1 text-xs font-semibold uppercase tracking-wide text-on-secondary-container">
                            {pos.level}
                          </span>
                          {pos.direction && (
                            <span className="flex items-center gap-1 text-sm text-on-surface-variant">
                              <BuildingOffice2Icon className="h-4 w-4" />
                              {pos.direction}
                            </span>
                          )}
                        </div>
                        <h3 className="mb-3 text-3xl font-semibold leading-tight text-primary transition-colors group-hover:text-primary-container md:text-4xl">
                          {pos.title}
                        </h3>
                        {pos.description &&
                          (isHtml(pos.description) ? (
                            <div
                              className="prose prose-sm max-w-3xl text-on-surface-variant"
                              dangerouslySetInnerHTML={{ __html: pos.description }}
                            />
                          ) : (
                            <p className="max-w-3xl leading-relaxed text-on-surface-variant">
                              {pos.description}
                            </p>
                          ))}
                      </div>

                      <div className="flex shrink-0 flex-col items-start justify-between border-t border-outline-variant/15 pt-4 lg:w-48 lg:items-end lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
                        <div className="mb-4 lg:mb-0 lg:text-right">
                          <div className="mb-1 text-xs uppercase tracking-wider text-on-surface-variant">
                            Deadline
                          </div>
                          <div className="text-sm font-medium text-on-surface">
                            {pos.deadline
                              ? formatChinaDate(pos.deadline, 'en-GB', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                })
                              : 'Open until filled'}
                          </div>
                        </div>
                        <Link
                          to={`/recruitment/${pos._id}`}
                          className="um-btn-link text-sm"
                        >
                          View details
                        </Link>
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>
          </div>
        </section>
      </main>
    </section>
  );
};

export default Recruitment;

